/**
 * Caché de sesión Analytics: sobrevive navegación SPA y F5 en la pestaña.
 * TTL 2 h. El botón Actualizar de la barra izquierda la vacía y recarga.
 */

const store = new Map();
const STORAGE_KEY = 'gic:analytics:session-cache:v2';
const MAX_PERSIST_CHARS = 1800000;

/** @type {number} */
export const ANALYTICS_CACHE_TTL_MS = 2 * 60 * 60 * 1000;

export const ANALYTICS_REFRESH_EVENT = 'analytics:refresh-all';

function persistNow_() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const entries = [];
    store.forEach((v, k) => {
      entries.push([k, v]);
    });
    entries.sort((a, b) => (b[1] && b[1].at ? b[1].at : 0) - (a[1] && a[1].at ? a[1].at : 0));
    let json = JSON.stringify(entries);
    while (json.length > MAX_PERSIST_CHARS && entries.length > 1) {
      entries.pop();
      json = JSON.stringify(entries);
    }
    if (json.length > MAX_PERSIST_CHARS) {
      sessionStorage.removeItem(STORAGE_KEY);
      return;
    }
    sessionStorage.setItem(STORAGE_KEY, json);
  } catch (e) {
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch (e2) {
      /* ignore */
    }
  }
}

function hydrate_() {
  try {
    if (typeof sessionStorage === 'undefined') return;
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const entries = JSON.parse(raw);
    if (!Array.isArray(entries)) return;
    const now = Date.now();
    entries.forEach((pair) => {
      const k = pair && pair[0];
      const v = pair && pair[1];
      if (!k || !v || v.at == null) return;
      if (now - Number(v.at) > ANALYTICS_CACHE_TTL_MS) return;
      store.set(String(k), { data: v.data, at: Number(v.at) });
    });
  } catch (e) {
    /* ignore */
  }
}

hydrate_();

/**
 * @param {string} key
 * @param {number} [ttlMs]
 * @returns {{ data: *, at: number }|null}
 */
export function cacheGet(key, ttlMs) {
  const hit = store.get(String(key));
  if (!hit) return null;
  const ttl = ttlMs != null ? ttlMs : ANALYTICS_CACHE_TTL_MS;
  if (Date.now() - hit.at > ttl) {
    store.delete(String(key));
    persistNow_();
    return null;
  }
  return hit;
}

/**
 * @param {string} key
 * @param {*} data
 */
export function cacheSet(key, data) {
  store.set(String(key), { data, at: Date.now() });
  persistNow_();
}

/**
 * @param {string} key
 */
export function cacheInvalidate(key) {
  if (key) store.delete(String(key));
  persistNow_();
}

/**
 * @param {string} prefix
 */
export function cacheInvalidatePrefix(prefix) {
  const p = String(prefix || '');
  Array.from(store.keys()).forEach((k) => {
    if (k.indexOf(p) === 0) store.delete(k);
  });
  persistNow_();
}

export function cacheClearAll() {
  store.clear();
  try {
    if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(STORAGE_KEY);
  } catch (e) {
    /* ignore */
  }
}

/** Vacía caché local y avisa a las pantallas montadas para que recarguen. */
export function requestAnalyticsRefresh() {
  cacheClearAll();
  try {
    window.dispatchEvent(new CustomEvent(ANALYTICS_REFRESH_EVENT));
  } catch (e) {
    /* ignore */
  }
}

/**
 * Carga con caché de sesión.
 * @template T
 * @param {string} key
 * @param {() => Promise<T>} fetcher
 * @param {{ force?: boolean, ttlMs?: number }} [opts]
 * @returns {Promise<T>}
 */
export async function withSessionCache(key, fetcher, opts) {
  const force = !!(opts && opts.force);
  const ttlMs = opts && opts.ttlMs != null ? opts.ttlMs : ANALYTICS_CACHE_TTL_MS;
  if (!force) {
    const hit = cacheGet(key, ttlMs);
    if (hit) {
      const data = hit.data;
      if (data && typeof data === 'object') {
        return { ...data, _fromCache: true, _cachedAt: hit.at };
      }
    }
  } else {
    cacheInvalidate(key);
  }
  const fresh = await fetcher();
  cacheSet(key, fresh);
  if (fresh && typeof fresh === 'object') {
    return { ...fresh, _fromCache: false, _cachedAt: Date.now() };
  }
  return fresh;
}
